import React, { useEffect, useState } from 'react';
import { wixClient } from '../config/wix.mjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackAuthSuccess } from '../lib/authTelemetry';
import { mintSnapshotEntitlements } from '../lib/snapshotEntitlements';
import { fetchServerSession, persistLocalWixTokens, startServerSession } from '../lib/sessionAuth';

const OAUTH_STORAGE_KEY = 'oauthRedirectData';

const withTimeout = async (promise, timeoutMs, message) => {
  let timeoutHandle = null;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutHandle = setTimeout(() => reject(new Error(message)), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  }
};

const getAccessTokenValue = (tokens) => tokens?.accessToken?.value || tokens?.access_token || null;

const getRefreshTokenValue = (tokens) => tokens?.refreshToken?.value || tokens?.refresh_token || null;

const normalizeTokens = (tokens) => {
  const accessValue = getAccessTokenValue(tokens);
  const refreshValue = getRefreshTokenValue(tokens);
  return {
    ...tokens,
    accessToken: tokens?.accessToken || (accessValue ? { value: accessValue } : undefined),
    refreshToken: tokens?.refreshToken || (refreshValue ? { value: refreshValue } : undefined),
  };
};

const normalizeRedirectPath = (value) => {
  if (!value) return '/';
  const trimmed = String(value).trim();
  if (!trimmed.startsWith('/')) return '/';
  if (trimmed.startsWith('//')) return '/';
  return trimmed;
};

const parseCallbackParams = () => {
  try {
    const parsed = wixClient.auth.parseFromUrl(window.location.href);
    if (parsed?.code || parsed?.state || parsed?.error) {
      return parsed;
    }
  } catch {
    // Fall back to manual parsing if the SDK parser fails.
  }

  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));

  return {
    code: urlParams.get('code') || hashParams.get('code'),
    state: urlParams.get('state') || hashParams.get('state'),
    error: urlParams.get('error') || hashParams.get('error') || undefined,
    errorDescription:
      urlParams.get('error_description') ||
      hashParams.get('error_description') ||
      urlParams.get('errorDescription') ||
      hashParams.get('errorDescription') ||
      undefined,
  };
};

export function LoginCallback() {
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const { setUserInfo, setIsPremium } = useAuth();

  useEffect(() => {
    async function handleCallback() {
      // Prevent multiple processing attempts
      if (isProcessing) return;
      setIsProcessing(true);

      try {
        const storedData = localStorage.getItem(OAUTH_STORAGE_KEY) || sessionStorage.getItem(OAUTH_STORAGE_KEY);

        let oauthData = null;
        try {
          oauthData = JSON.parse(storedData);
        } catch (e) {
          console.error('Failed to parse stored OAuth data:', e);
        }

        const {
          code,
          state,
          error: callbackError,
          errorDescription: callbackErrorDescription,
        } = parseCallbackParams();

        if (callbackError) {
          throw new Error(callbackErrorDescription || `Google authorization failed: ${callbackError}`);
        }

        // Only process if we have a code and state
        if (!code || !state) {
          // If we're on the homepage without params, just return silently
          if (window.location.pathname === '/') {
            return;
          }
          throw new Error('No authorization code or state received');
        }

        if (!oauthData) {
          throw new Error('No OAuth data found in storage');
        }

        if (state !== oauthData.state) {
          throw new Error('State mismatch - possible security risk');
        }

        let tokens = await withTimeout(
          wixClient.auth.getMemberTokens(code, state, oauthData),
          10000,
          'Google token exchange timed out. Please try again.'
        );

        let retries = 0;
        while (!getAccessTokenValue(tokens) && retries < 3) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          tokens = await withTimeout(
            wixClient.auth.getMemberTokens(code, state, oauthData),
            10000,
            'Google token exchange timed out. Please try again.'
          );
          retries += 1;
        }

        if (!getAccessTokenValue(tokens)) {
          throw new Error('Failed to complete Google sign-in. Please try again.');
        }

        const normalizedTokens = normalizeTokens(tokens);
        wixClient.auth.setTokens(normalizedTokens);
        persistLocalWixTokens(normalizedTokens);

        const currentMember = await wixClient.members
          .getCurrentMember({ fieldsets: ['FULL'] })
          .catch(async () => wixClient.members.getCurrentMember());
        
        if (!currentMember || !currentMember.member) {
          throw new Error('Failed to get member data after login');
        }

        const memberData = currentMember.member;
        
        // Try multiple paths to get the email
        const email = memberData.loginEmail || 
                     memberData.profile?.email || 
                     memberData.contact?.emails?.[0] ||
                     memberData.primaryEmail;
                     
        const name = memberData.profile?.nickname || 
                    memberData.profile?.firstName ||
                    null;

        if (!email) {
          console.warn('No email found in member data');
        }

        let resolvedEmail = email;
        let resolvedName = name;
        let resolvedToken = getAccessTokenValue(normalizedTokens);
        let resolvedPremium = null;

        const sessionState = await startServerSession(normalizedTokens).catch(() => null);
        if (sessionState?.accessToken) {
          resolvedEmail = sessionState.userEmail || email;
          resolvedName = sessionState.userName || name;
          resolvedToken = sessionState.accessToken;
          resolvedPremium = sessionState.isPremium;
          setIsPremium(sessionState.isPremium);
        }

        // Set user info in context
        setUserInfo(resolvedEmail, resolvedName, resolvedToken);

        try {
          await mintSnapshotEntitlements(resolvedToken || null);
        } catch (snapshotError) {
          console.warn('Failed to mint snapshot entitlements during login callback:', snapshotError);
        }

        trackAuthSuccess({
          eventType: 'login',
          email: resolvedEmail,
          method: 'google_oauth',
          source: 'login_callback',
          accessToken: resolvedToken || null,
          isPremium: Boolean(resolvedPremium)
        });

        // Check premium status if email is available
        if (typeof resolvedPremium === 'boolean') {
          setIsPremium(resolvedPremium);
        } else {
          try {
            const refreshedSession = await fetchServerSession({ refresh: true });
            setIsPremium(Boolean(refreshedSession?.isPremium));
          } catch (error) {
            console.error('Error checking premium status:', error);
            setIsPremium(false);
          }
        }

        // Clean up stored data
        localStorage.removeItem(OAUTH_STORAGE_KEY);
        sessionStorage.removeItem(OAUTH_STORAGE_KEY);
        
        // Navigate to the original URI or home page
        const redirectTo = normalizeRedirectPath(oauthData?.originalUri || '/');
        try {
          navigate(redirectTo, { replace: true });
        } catch (navError) {
          console.error('Navigation error:', navError);
          window.location.href = redirectTo;
        }
      } catch (err) {
        console.error('Social login callback error:', err);
        setError(err.message || 'Failed to complete social login. Please try again.');
        
        // Clean up stored data on error
        localStorage.removeItem(OAUTH_STORAGE_KEY);
        sessionStorage.removeItem(OAUTH_STORAGE_KEY);
      } finally {
        setIsProcessing(false);
      }
    }

    handleCallback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, setUserInfo, setIsPremium]);

  // Only show error UI if we're on the callback page
  if (error && window.location.pathname === '/login-callback') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button 
            onClick={() => {
              try {
                navigate('/login', { replace: true });
              } catch (navError) {
                window.location.href = '/login';
              }
            }}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  // Only show loading UI if we're on the callback page
  if (window.location.pathname === '/login-callback') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div>Completing social login...</div>
        </div>
      </div>
    );
  }

  return null;
} 
